// components/DynamicPage.tsx
import React from "react";
import type { PageConfig } from "../../../../types";
import "./Layouts.scss";
import { IconInfoCircle } from "@tabler/icons-react";
import { Button, Popover } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import DynamicGroup from "./DynamicGroup";

interface DynamicPageProps {
  pageConfig: PageConfig;
  review?: boolean; // Add the optional review prop
  taxYear: number; // REQUIRED - tax year for organizer
}

/**
 * DynamicPage component to render fields based on the page configuration.
 * uses the Dynamic Group component to render fields based on the page configuration, passes in an initialGroupCount for pages that need it like dependents
 *
 * @param {DynamicPageProps} props - The props for the component, including the page configuration and optional review mode.
 * @returns {JSX.Element} The rendered JSX element.
 */
const DynamicPage: React.FC<DynamicPageProps> = ({
  pageConfig,
  review,
  taxYear,
}) => {
  const [opened, { close, open }] = useDisclosure(false);

  const popoverContent = (
    <div className="popover-content">
      <h3 style={{ textAlign: "center" }}>{pageConfig?.info?.title}</h3>
      <ul>
        {pageConfig?.info?.list.map((item, index) => {
          const firstWord = item.split(":")[0];
          const restOfItem = item.slice(firstWord.length);
          return (
            <li className="list-item" key={`${item}-${index}`}>
              <span style={{ fontWeight: "700" }} className="first-word">
                {firstWord}
              </span>
              {restOfItem}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div className={`dynamic-page ${pageConfig.layout ?? "default"}`}>
      {pageConfig.title && (
        <span className="page-title">
          <div className="title-container">
            <h2>{pageConfig.title}</h2>
            {pageConfig.description && (
              <p className="page-description">
                {pageConfig.description.beforeBoldText && (
                  <span className="before-bold-text">
                    {pageConfig.description.beforeBoldText}
                  </span>
                )}
                {pageConfig.description.boldText && (
                  <b className="bold-text">
                    {" "}
                    {pageConfig.description.boldText}
                  </b>
                )}
                {pageConfig.description.afterBoldText && (
                  <span className="after-bold-text">
                    {" "}
                    {pageConfig.description.afterBoldText}
                  </span>
                )}
              </p>
            )}
          </div>
          {pageConfig.info && (
            <Popover
              width={550}
              withArrow
              position="bottom"
              shadow="md"
              opened={opened}
            >
              <Popover.Target>
                <Button
                  className="info-button"
                  onMouseEnter={open}
                  onMouseLeave={close}
                >
                  <IconInfoCircle size={30} />
                </Button>
              </Popover.Target>
              <Popover.Dropdown style={{ borderRadius: "0.5rem" }}>
                {popoverContent}
              </Popover.Dropdown>
            </Popover>
          )}
        </span>
      )}
      <div className="fields-container">
        {pageConfig.fields?.map((field, index) => {
          return (
            <DynamicGroup
              key={field.id || field.name || index}
              groupField={field}
              disabled={review}
              taxYear={taxYear}
            />
          );
        })}
      </div>
    </div>
  );
};

export default DynamicPage;
